/* eslint-disable no-nested-ternary */
import type { CardItemBadge, CardItemBreederProps } from '@presentation/components';
import { CardItem } from '@presentation/components';
import {
  COLOR_PER_DOG_BREEDER_LAST_CONNECTED_TYPE,
  ICON_PER_DOG_BREEDER_LAST_CONNECTED_TYPE,
} from '@presentation/constants';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { useMemo } from 'react';
import { IconScaleStars } from '../IconScaleStars';
import { Skeleton } from './components/Skeleton';
import * as Styled from './styled';

export * from './types';

export const CardItemBreeder: FC<CardItemBreederProps> = (props) => {
  const { translate } = useTranslation();
  const {
    image,
    hasLitters,
    lastConnectedBadgeType,
    name,
    location,
    breeds,
    languages,
    rating,
    loading = false,
    lazyLoadImage = true,
    dogsPedigree,
    breedClubValidated,
    ...rest
  } = props;

  const content = {
    title: name,
    subtitle: breeds,
    text: location,
  };

  const badges = lastConnectedBadgeType
    ? [
        {
          label: translate(`enum.lastConnectedType.${lastConnectedBadgeType}`),
          icon: ICON_PER_DOG_BREEDER_LAST_CONNECTED_TYPE[lastConnectedBadgeType],
          color: COLOR_PER_DOG_BREEDER_LAST_CONNECTED_TYPE[lastConnectedBadgeType],
        } as CardItemBadge,
      ]
    : [];

  const subtitleBadges = useMemo(() => {
    const array = [];
    if (dogsPedigree === 'FCI') {
      array.push({ label: translate('badges.pedigreeFci') });
    } else if (dogsPedigree) {
      array.push({ label: translate('badges.pedigree') });
    }
    if (breedClubValidated) {
      array.push({ label: translate('badges.breedClub') });
    }
    return array;
  }, [breedClubValidated, dogsPedigree, translate]);

  if (loading) return <Skeleton />;

  return (
    <CardItem
      lazyLoadImage={lazyLoadImage}
      image={image}
      content={content}
      badges={badges}
      subtitleBadges={subtitleBadges}
      label={
        hasLitters
          ? {
              content: translate('glossary.litterAvailable'),
              theme: 'blue',
              size: 'small',
            }
          : undefined
      }
      footer={
        !!languages || !!rating?.amount ? (
          <>
            <Styled.FooterText hasReviews={!!rating?.amount}>{languages}</Styled.FooterText>
            {!!rating?.amount && (
              <Styled.ReviewWrapper>
                <IconScaleStars amount={rating.average} />
                <Styled.ReviewText>({rating.amount})</Styled.ReviewText>
              </Styled.ReviewWrapper>
            )}
          </>
        ) : undefined
      }
      {...rest}
    />
  );
};
